import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Download, Loader, Mail } from 'lucide-react';
import { COLORS, getColor } from '../utils/colors';

// Enhanced lead generation form component with improved feedback and webhook integration
const LeadForm = ({ trendTitle = "", minimal = false, color = "primary" }) => {
  // Form fields
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  
  // Form state
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const colorStyle = getColor(color);
  
  // Validate email with regex pattern
  const validateEmail = (email) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };
  
  // Handle form submission with improved error handling
  const handleSubmit = async () => {
    // Reset form state
    setError('');
    setSuccessMessage('');
    
    // Validate form fields
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid work email');
      return;
    }
    
    if (!company.trim()) {
      setError('Please enter your company name');
      return;
    }
    
    // Update form status to submitting (shows loading indicator)
    setStatus('submitting');
    
    try {
      // Prepare form data
      const formData = {
        name,
        email,
        company,
        jobTitle: jobTitle || '(Not provided)',
        formType: trendTitle ? `Trend Report: ${trendTitle}` : 'General Innovation Insights',
        submittedAt: new Date().toISOString(),
      };
      
      // Use the production webhook URL
      const webhookUrl = 'https://hook.eu2.make.com/fav4o8kusfjga07yzqdu9ld3npzxlp49';
      
      // Make the API call with proper CORS headers and error handling
      try {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(formData),
          mode: 'cors', // Try with cors mode first
        });
        
        // If the server returns an error status
        if (!response.ok) {
          // Try to get error details from response if available
          let errorMessage;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || `Server error: ${response.status}`;
          } catch (e) {
            // If can't parse error JSON
            errorMessage = `Server error: ${response.status}`;
          }
          throw new Error(errorMessage);
        }
        
        // Try to parse the success response
        try {
          const responseData = await response.json();
          
          // If the API explicitly returns success: false in the response body
          if (responseData.success === false) {
            throw new Error(responseData.error || 'Form submission failed');
          }
          
          // Handle success
          setStatus('success');
          setSuccessMessage(trendTitle 
            ? `Thank you! Your ${trendTitle} report is on its way to your inbox.` 
            : 'Thank you! We\'ve received your request and will send the insights to your inbox.');
          
          // Clear form data after successful submission
          setName('');
          setEmail('');
          setCompany('');
          setJobTitle('');
          
        } catch (parseError) {
          // If we can't parse the response as JSON, but the HTTP status was OK,
          // we still consider it a success (some webhooks don't return JSON)
          if (response.ok) {
            setStatus('success');
            setSuccessMessage(trendTitle 
              ? `Thank you! Your ${trendTitle} report is on its way to your inbox.` 
              : 'Thank you! We\'ve received your request and will send the insights to your inbox.');
            
            // Clear form data after successful submission
            setName('');
            setEmail('');
            setCompany('');
            setJobTitle('');
          } else {
            throw new Error('Unable to process the server response');
          }
        }
        
      } catch (fetchError) {
        // Handle fetch errors (CORS, network, etc.)
        console.error('API call failed:', fetchError);
        
        // If it's likely a CORS error, try with a no-cors fallback
        if (fetchError.message.includes('CORS') || fetchError.message.includes('Failed to fetch')) {
          console.log('Attempting no-cors fallback...');
          
          // This is a fallback that won't give us response data but might succeed in sending the data
          try {
            await fetch(webhookUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(formData),
              mode: 'no-cors',
            });
            
            // If we get here, the request didn't throw, but we don't know if it succeeded
            // Let's assume success for better UX
            setStatus('success');
            setSuccessMessage(trendTitle 
              ? `Thank you! Your ${trendTitle} report is on its way to your inbox.` 
              : 'Thank you! We\'ve received your request and will send the insights to your inbox.');
            
            // Clear form data
            setName('');
            setEmail('');
            setCompany('');
            setJobTitle('');
            
          } catch (nocorsError) {
            console.error('Even no-cors fallback failed:', nocorsError);
            throw new Error('Network error: Unable to submit form. Please try again later.');
          }
        } else {
          // For non-CORS errors, re-throw
          throw fetchError;
        }
      }
      
    } catch (err) {
      // Handle all other errors
      console.error('Form submission error:', err);
      setStatus('error');
      setError(err.message || 'There was a problem submitting your request. Please try again.');
    }
  };
  
  // Show success message when form submitted successfully
  if (status === 'success') {
    return (
      <div className="text-center p-6 bg-green-50 rounded-xl border border-green-100 shadow-sm">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-500" />
        </div>
        <h3 className="font-semibold text-green-900 text-xl mb-2">Thank You!</h3>
        <p className="text-green-700 mb-4">
          {successMessage}
        </p>
        <button 
          onClick={() => setStatus('idle')}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
        >
          Submit another request
        </button>
      </div>
    );
  }
  
  return (
    <div className={`rounded-xl border ${colorStyle?.border || 'border-gray-200'} ${minimal ? 'p-4' : 'p-6'} bg-white shadow-sm`}>
      {!minimal && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center`} style={{ backgroundColor: COLORS.primary }}>
              {trendTitle ? <Download className="w-4 h-4 text-white" /> : <Mail className="w-4 h-4 text-white" />}
            </div>
            <h3 className="font-bold text-gray-900 text-lg">
              {trendTitle ? `Get the ${trendTitle} Report` : "Get Innovation Insights"}
            </h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Complete this form to receive our detailed analysis in your inbox
          </p>
        </>
      )}
      
      <div className="space-y-3">
        <input
          type="text"
          placeholder="Full name"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/30 focus:outline-none"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={status === 'submitting'}
          aria-label="Full name"
        />
        
        <input
          type="email"
          placeholder="Work email"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/30 focus:outline-none"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'submitting'}
          aria-label="Work email"
        />
        
        <input
          type="text"
          placeholder="Company name"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/30 focus:outline-none"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={status === 'submitting'}
          aria-label="Company name"
        />
        
        <input
          type="text"
          placeholder="Job title (optional)"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/30 focus:outline-none"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          disabled={status === 'submitting'}
          aria-label="Job title"
        />
        
        {error && (
          <div className="text-red-500 text-sm flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}
        
        <button 
          onClick={handleSubmit}
          disabled={status === 'submitting'}
          className="w-full py-3 text-white rounded-lg font-medium transition-transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
          style={{ backgroundColor: COLORS.primary }}
          aria-label={trendTitle ? "Get This Report" : "Start For Free"}
        >
          {status === 'submitting' ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <span>{trendTitle ? "Get This Report" : "Start For Free"}</span>
          )}
        </button>
        
        <p className="text-xs text-gray-500 text-center">
          We respect your privacy and will never share your information.
        </p>
      </div>
    </div>
  );
};

export default LeadForm;